import FilePresentOutlined from '@mui/icons-material/FilePresentOutlined';
import OpenInNew from '@mui/icons-material/OpenInNew';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import { IconButton, List, ListItemButton, ListItemIcon, ListItemText, Tooltip } from '@mui/material';
import { FileOutput } from 'dtos/diligence/FileOutput';
import { observer } from 'mobx-react-lite';
import { DateTime } from 'luxon';
import { DeleteConfirmModal } from 'components/diligence/modals';
import { useState } from 'react';
import { noThumbnailAvailable } from 'dtos/diligence/common';

function renderSize(size: number): string {
  if (size > 1048576) {
    return +(size / 1048576).toFixed(1) + ' MB';
  }
  return +(size / 1024).toFixed(1) + ' KB';
}

function renderDate(date: string): string {
  return DateTime.fromISO(date).toLocal().toFormat('MM-dd-yyyy');
}

function renderUrl(url: string): string {
  return url;
}

type FilesListOldProps = {
  style?: any;
  latestDate?: DateTime;
  files: FileOutput[];
  onDelete: (file: FileOutput) => Promise<void>;
};

const FilesListOld = observer(({ style, files, latestDate, onDelete }: FilesListOldProps) => {
  const [confirmDeleteVisible, setConfirmDeleteVisible] = useState(false);
  const [confirmDeleteFile, setConfirmDeleteFile] = useState<FileOutput>();
  const [deleting, setDeleting] = useState(false);

  const handleDeleteClick = (event: React.MouseEvent, file: FileOutput) => {
    setTimeout(() => {
      // let tooltip hide itself first
      setDeleting(false);
      setConfirmDeleteFile(file);
      setConfirmDeleteVisible(true);
    }, 1);
    event.preventDefault();
  };

  const handleDelete = async () => {
    if (!confirmDeleteFile) return;
    setDeleting(true);
    await onDelete(confirmDeleteFile);
    setConfirmDeleteVisible(false);
  };

  return (
    <>
      <DeleteConfirmModal
        visible={confirmDeleteVisible}
        disabled={deleting}
        file={confirmDeleteFile}
        onClose={() => setConfirmDeleteVisible(false)}
        onConfirm={handleDelete}
      />
      <div
        style={{
          border: '1px solid rgba(34, 34, 34, 0.23)',
          borderTopLeftRadius: 4,
          borderTopRightRadius: 4,
          backgroundColor: 'rgba(250, 250, 250, 1)',
          padding: '24px',
          lineHeight: 'initial',
          ...style,
        }}
        data-testid="files-stats"
      >
        <strong>
          {files.length} Files{latestDate && ' • Last Activity ' + latestDate?.toLocaleString(DateTime.DATE_MED)}
        </strong>
      </div>
      <List
        sx={{
          bgcolor: 'background.paper',
          padding: 0,
          border: '1px solid rgba(34, 34, 34, 0.23)',
          borderBottomLeftRadius: 4,
          borderBottomRightRadius: 4,
          borderTop: 0,
        }}
        data-testid="files-list"
      >
        {files.map((file, idx) => (
          <ListItemButton
            key={file.id}
            component="a"
            href={renderUrl(file.url)}
            target="_blank"
            sx={{
              padding: '16px 24px 0 24px',
              '&:first-of-type': {
                paddingTop: '24px',
              },
              '&:last-of-type': {
                paddingBottom: '8px',
                borderBottom: 'none',
                '& > div': {
                  borderBottom: 'none !important',
                },
              },
            }}
          >
            <div
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                paddingBottom: '16px',
                borderBottom: '1px solid rgba(34, 34, 34, 0.23)',
              }}
            >
              <ListItemIcon sx={{ minWidth: 'initial', marginRight: '16px' }}>
                {file.thumbnail && file.thumbnail !== noThumbnailAvailable ? (
                  <div style={{ minWidth: 32 }}>
                    <img src={'data:image/jpeg;base64, ' + file.thumbnail} alt="thumbnail" />
                  </div>
                ) : (
                  <FilePresentOutlined fontSize="large" />
                )}
              </ListItemIcon>
              <ListItemText
                primary={
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    {file.name}
                    <OpenInNew sx={{ fontSize: '16px', marginLeft: '6px' }} />
                  </div>
                }
                secondary={renderSize(file.size) + ' - ' + renderDate(file.lastModified)}
                sx={{
                  margin: 0,
                  whiteSpace: 'nowrap',
                  '& > span': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  },
                  '& > p': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  },
                }}
              />
              <Tooltip title="Delete">
                <IconButton
                  aria-label="delete"
                  data-testid="file-delete"
                  onClick={(event) => handleDeleteClick(event, file)}
                >
                  <DeleteOutline />
                </IconButton>
              </Tooltip>
            </div>
          </ListItemButton>
        ))}
      </List>
    </>
  );
});

export { FilesListOld };
